/**
 * Visibility rules for the sandbox surface, kept pure so they can be tested
 * without a hook harness (this repo's `useProfileQuery` is a leaked-mock victim,
 * so a `renderHook` test of the wired hook passes alone and lies in the full
 * suite — see CLAUDE.md failure mode 17).
 */

export interface SandboxTabInputs {
  /** PostHog flag, off by default. Per-market rollout rides this, not region. */
  isVoiceSandboxEnabled: boolean
  /** PlanType.FREE_TRIAL. Sandbox replaces the old free-trial credit grant. */
  isTrial: boolean
  /**
   * Whether the profile query has resolved. `isTrial` is false while it is in
   * flight, so without this the tab pops in after first paint and reflows the
   * tab strip - and a direct /phone-numbers/sandbox-numbers link would mount the
   * sheet against a plan we have not read yet.
   */
  isPlanLoaded: boolean
  /**
   * `call_channel` view, from the org permission map.
   *
   * The proxy behind this tab enforces CallChannel View/Edit/Delete server-side
   * (contacto-core), so a role without it gets a 403 on the very first list
   * call. Manager is view-only and Specialist/Support/Finance/UCC hold nothing
   * on this module, so without this they would open a tab that can only fail.
   */
  hasCallChannelView: boolean
}

/**
 * The tab itself is deliberately NOT region-gated: sandbox replaces the free-trial
 * credit grant in every market, and India is the primary market rather than the
 * only one. Region only decides whether the compliance-unlock comparison renders
 * (see `showsComplianceComparison`).
 */
export const canViewSandboxTab = ({
  isVoiceSandboxEnabled,
  isTrial,
  isPlanLoaded,
  hasCallChannelView,
}: SandboxTabInputs): boolean =>
  isVoiceSandboxEnabled && isPlanLoaded && isTrial && hasCallChannelView

/**
 * The compliance-unlock story is India-only. The documents (COI/Udyam, PAN/GST)
 * and the compliance route are India-specific, and
 * Phone Numbers only mounts the compliance sheet for the Indian region — so
 * offering it elsewhere would show foreign paperwork behind a dead link.
 */
export const showsComplianceComparison = (isIndianRegion: boolean): boolean => isIndianRegion

/**
 * The signup / 2FA number to show as a read-only row, or undefined for none.
 *
 * number_service returns the onboarding row whenever `phone_number_verified` is
 * true and never checks the number itself is non-empty, so live accounts come
 * back as `{phone_number: "", verified: true}`. Keying on the row existing then
 * renders a blank row with a placeholder flag AND suppresses the "no verified
 * destinations yet" empty state, because the object is truthy. Key on the
 * number.
 */
export const resolveSignupNumber = <T extends { phone_number: string }>(
  signupNumber?: T | null,
): T | undefined => (signupNumber?.phone_number ? signupNumber : undefined)

/**
 * The tab to actually select, given the one the URL asked for.
 *
 * A denied or stale deep link must still render a usable page: selecting
 * `sandbox-numbers` when the gate says no leaves the page with no matching
 * trigger, content or sheet, i.e. a blank body. The gate should take the
 * feature away, not the page. Only sandbox is normalised here - the other tabs
 * own their own fallbacks.
 */
export const resolveSandboxTabSelection = <T extends string>(
  requestedTab: T,
  showSandboxTab: boolean,
  fallbackTab: T,
): T => (requestedTab === 'sandbox-numbers' && !showSandboxTab ? fallbackTab : requestedTab)
